/**
 * FERP Portal - Global Error Store
 *
 * Persistent error store for GraphQL errors with full context.
 * Stores last 1,000 errors (~500KB localStorage, 24-hour TTL).
 *
 * **Persistence Strategy**:
 * - Errors stored in localStorage (survives page refresh)
 * - 24-hour TTL (auto-cleanup of old errors)
 * - Browser-session scoped (clears when browser closes)
 *
 * **Single Code Path Integration**:
 * This store is populated by the Apollo Client error link (apollo-client.ts),
 * ensuring all GraphQL errors flow through one capture point.
 *
 * **Complements Prometheus**:
 * - Prometheus: Error counts and rates (metrics)
 * - This store: Actual error messages, stack traces, query context (logs)
 */

import { GraphQLError, Kind, type OperationDefinitionNode } from 'graphql';
import type { Operation } from '@apollo/client';
import { isDevModeEnabled } from '@/utils/env';
import { getCurrentCorrelationId } from './apollo-client';

export interface DetailedError {
  id: string;
  timestamp: Date;

  // DRR-228: Correlation ID for E2E tracing
  correlationId?: string;

  // Error classification
  code: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';

  // GraphQL context
  operationType: 'query' | 'mutation' | 'subscription';
  operationName: string;
  path?: string[]; // e.g., ["getTasks", "0", "metadata"]
  locations?: Array<{ line: number; column: number }>;

  // Backend extensions (from GraphQL error.extensions)
  serviceName?: string;
  stackTrace?: string[];

  // Request context (variables - sanitized for PII)
  variableKeys?: string[]; // Variable names only, no values
}

class GlobalErrorStore {
  private errors: DetailedError[] = [];
  private readonly MAX_ERRORS = 1000;
  private readonly STORAGE_KEY = 'ferp:errorStore:captures';
  private readonly TTL_HOURS = 24;

  constructor() {
    // Load persisted errors from localStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Load errors from localStorage (survives page refresh)
   */
  private loadFromStorage(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored);

      // Validate structure
      if (!Array.isArray(parsed)) {
        console.warn('Invalid error store data in localStorage, clearing');
        localStorage.removeItem(this.STORAGE_KEY);
        return;
      }

      // Filter out expired errors (older than TTL)
      const now = new Date();
      const ttlMs = this.TTL_HOURS * 60 * 60 * 1000;

      this.errors = parsed
        .map(e => ({
          ...e,
          timestamp: new Date(e.timestamp), // Rehydrate Date objects
        }))
        .filter(e => now.getTime() - e.timestamp.getTime() < ttlMs)
        .slice(0, this.MAX_ERRORS); // Enforce limit

      if (isDevModeEnabled()) console.log(`📦 Loaded ${this.errors.length} errors from localStorage (${parsed.length - this.errors.length} expired)`);
    } catch (error) {
      console.error('Failed to load errors from localStorage:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Save errors to localStorage
   */
  private saveToStorage(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.errors));
    } catch (error) {
      console.error('Failed to save errors to localStorage:', error);
      // If quota exceeded, clear old errors and retry
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.errors = this.errors.slice(0, 50); // Keep only 50 most recent
        try {
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.errors));
        } catch (retryError) {
          console.error('Failed to save errors even after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Add a captured error (persisted to localStorage)
   */
  add(error: DetailedError): void {
    this.errors.unshift(error); // Newest first
    if (this.errors.length > this.MAX_ERRORS) {
      this.errors = this.errors.slice(0, this.MAX_ERRORS);
    }

    // Persist to localStorage
    this.saveToStorage();

    // Log capture for debugging
    console.log(
      `🔴 FERP Error Captured [${this.errors.length}/${this.MAX_ERRORS}]:`,
      `${error.code} - ${error.operationType} ${error.operationName}`,
      { message: error.message, severity: error.severity }
    );
  }

  /**
   * Get all captured errors
   */
  getAll(): DetailedError[] {
    return [...this.errors];
  }

  /**
   * Get errors by code
   */
  getByCode(code: string): DetailedError[] {
    return this.errors.filter(e => e.code === code);
  }

  /**
   * Get recent errors
   */
  getRecent(count: number): DetailedError[] {
    return this.errors.slice(0, count);
  }

  /**
   * Clear all errors (both in-memory and localStorage)
   */
  clear(): void {
    this.errors = [];

    // Clear from localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(this.STORAGE_KEY);
        console.log('🗑️ Cleared error store from localStorage');
      }
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  /**
   * Get error statistics
   */
  getStats() {
    return {
      total: this.errors.length,
      byCode: this.groupBy(this.errors, e => e.code),
      bySeverity: this.groupBy(this.errors, e => e.severity),
      byOperation: this.groupBy(this.errors, e => e.operationName),
    };
  }

  private groupBy<T>(items: T[], keyFn: (item: T) => string): Record<string, number> {
    return items.reduce((acc, item) => {
      const key = keyFn(item);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }
}

export const globalErrorStore = new GlobalErrorStore();

/**
 * Determine error severity based on code
 */
export function getErrorSeverity(code: string): DetailedError['severity'] {
  // Critical: Backend 5xxx errors or validation failures (life-critical)
  if (code.startsWith('FERP-5') || code === 'GRAPHQL_VALIDATION_FAILED') {
    return 'critical';
  }

  // High: Backend 4xxx errors
  if (code.startsWith('FERP-4')) {
    return 'high';
  }

  // Medium: Most validation errors
  if (code.includes('VALIDATION')) {
    return 'medium';
  }

  return 'low';
}

/**
 * Extract operation type from an Apollo Operation's query definitions.
 */
function getOperationType(operation: Operation): 'query' | 'mutation' | 'subscription' {
  const def = operation.query.definitions[0];
  if (def?.kind === Kind.OPERATION_DEFINITION) {
    return (def as OperationDefinitionNode).operation;
  }
  return 'query';
}

/**
 * Create DetailedError from GraphQL error
 */
export function createDetailedError(
  graphQLError: GraphQLError,
  operation: Operation
): DetailedError {
  const code = (graphQLError.extensions?.code as string) || 'UNKNOWN';
  const operationType = getOperationType(operation);

  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    // DRR-228: Include correlation ID for E2E tracing
    correlationId: getCurrentCorrelationId() ?? undefined,
    code,
    message: graphQLError.message,
    severity: getErrorSeverity(code),
    operationType,
    operationName: operation.operationName || 'UnnamedOperation',
    path: graphQLError.path as string[] | undefined,
    locations: graphQLError.locations?.map(loc => ({
      line: loc.line,
      column: loc.column,
    })),
    serviceName: graphQLError.extensions?.serviceName as string | undefined,
    stackTrace: graphQLError.extensions?.stacktrace as string[] | undefined,
    variableKeys: operation.variables ? Object.keys(operation.variables) : undefined,
  };
}

/**
 * Create DetailedError from network error
 */
export function createNetworkError(
  networkError: Error & { statusCode?: number },
  operation: Operation
): DetailedError {
  const code = networkError.statusCode ? `HTTP_${networkError.statusCode}` : 'NETWORK_ERROR';
  const operationType = getOperationType(operation);

  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    // DRR-228: Include correlation ID for E2E tracing
    correlationId: getCurrentCorrelationId() ?? undefined,
    code,
    message: networkError.message,
    severity: networkError.statusCode && networkError.statusCode >= 500 ? 'critical' : 'high',
    operationType,
    operationName: operation.operationName || 'UnnamedOperation',
    variableKeys: operation.variables ? Object.keys(operation.variables) : undefined,
  };
}

/**
 * Expose globalErrorStore to window for debugging in development
 *
 * Usage in browser console:
 *   window.ferpDebug.errorStore.getAll()
 *   window.ferpDebug.errorStore.getByCode('GRAPHQL_VALIDATION_FAILED')
 *   window.ferpDebug.errorStore.clear()
 */
if (typeof window !== 'undefined' && isDevModeEnabled()) {
  window.ferpDebug = window.ferpDebug || {};
  window.ferpDebug.errorStore = globalErrorStore;
  console.log('🐛 FERP Debug: globalErrorStore available at window.ferpDebug.errorStore');
}
